<script setup>
import axios from 'axios';
import { BButton, BFormSelect, BLink, BTable } from 'bootstrap-vue-next';
import { ref, watch } from 'vue';

import ErrorState from '~/common/components/ErrorState.vue';
import MarkdownLinkRenderer from '~/common/components/MarkdownLinkRenderer.vue';
import PendingSubmissions from '~/common/components/PendingSubmissions.vue';
import RoutePagination from '~/common/components/RoutePagination.vue';
import ThemedOverlay from '~/common/components/ThemedOverlay.vue';
import UserWithGitLabProfileLink from '~/common/components/UserWithGitLabProfileLink.vue';
import { BONUS_POINT_ACTIVITY_TYPES } from '~/common/Constants';
import { dateFieldFormatter } from '~/common/FieldFormats';
import { useVueData } from '~/common/useVueData';

import ActionCard from './home-page/ActionCard.vue';
import AddPointsModal from './user-page/AddPointsModal.vue';

const { isAdmin: admin } = useVueData();

const bonusPoints = ref([]);
const pageData = ref({
  perPage: 50,
  totalRows: 0,
});
const busy = ref(true);
const showAddPointsModal = ref(false);
const error = ref(null);
const selectedActivityTypes = ref(
  Object.keys(BONUS_POINT_ACTIVITY_TYPES).filter(
    key => !['close', 'label'].includes(key),
  ),
);

const activityTypeOptions = Object.entries(BONUS_POINT_ACTIVITY_TYPES).map(
  ([value, text]) => ({ value, text }),
);

const fields = [
  {
    key: 'username',
    label: 'Username',
    tdClass: 'text-nowrap',
  },
  {
    key: 'reason',
    label: 'Reason',
  },
  {
    key: 'activity_type',
    label: 'Activity Type',
    formatter: ({ value }) => BONUS_POINT_ACTIVITY_TYPES[value],
  },
  {
    key: 'points',
    label: 'Points',
  },
  {
    key: 'awarded_by',
    label: 'Issued by',
  },
  {
    key: 'created_at',
    label: 'Issued date',
    formatter: dateFieldFormatter,
    tdClass: 'text-nowrap',
  },
];

const fetchData = async (page) => {
  busy.value = true;
  error.value = null;

  try {
    const response = await axios.get('/api/v1/bonus_points', {
      params: { page, activity_types: selectedActivityTypes.value },
    });
    const { records, metadata } = response.data;
    bonusPoints.value = records;
    pageData.value = {
      perPage: metadata.per_page,
      totalRows: metadata.total_count,
    };
  } catch (err) {
    error.value = err;
  } finally {
    busy.value = false;
  }
};

watch(selectedActivityTypes, () => fetchData(1), { deep: true });
fetchData(1);
</script>

<template>
  <ThemedOverlay :show="busy">
    <ErrorState v-if="error" />
    <template v-else>
      <PendingSubmissions
        v-if="admin"
        show-heading
      />
      <BButton
        v-if="admin"
        variant="outline-primary"
        data-test-id="issue-bonus-points-button"
        @click="showAddPointsModal = true"
      >
        Issue bonus points
      </BButton>
      <ActionCard
        title="Bonus Points"
        :to="{ name: 'documentation', params: { page: 'user-guide' }, hash: '#bonus-points' }"
        :target-blank="false"
        action-name="Read about bonus points in the user guide"
        class="my-3"
      >
        Earn bonus points for tackling special issues, labeling and closing issues,
        event participation, content creation, and more.
      </ActionCard>
      <BFormSelect
        v-model="selectedActivityTypes"
        :options="activityTypeOptions"
        multiple
        :select-size="8"
        class="mb-3"
      />

      <BTable
        :fields="fields"
        :items="bonusPoints"
        responsive="md"
      >
        <template #cell(username)="{ value }">
          <UserWithGitLabProfileLink :username="value" />
        </template>
        <template #cell(reason)="{ value }">
          <MarkdownLinkRenderer :value="value" />
        </template>
        <template #cell(awarded_by)="{ item, value }">
          <BLink
            is-unsafe-link
            :href="item.awarded_by_web_url"
            target="_blank"
          >
            {{ value }}
          </BLink>
        </template>
      </BTable>
      <RoutePagination
        :total-rows="pageData.totalRows"
        :per-page="pageData.perPage"
        @update:model-value="fetchData($event)"
      />
    </template>
  </ThemedOverlay>

  <AddPointsModal
    v-if="admin"
    v-model="showAddPointsModal"
  />
</template>
